/**
 * useHomeSLHomeStockBlocksLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait HomeSLHomeStockBlocks
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, HomeSLAssemblyView as HomeSLAssemblyViewEntity, HomeSLHomeStockBlocksEventPayloadMap } from '@app/shared';
import { useUIEvents } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';
import { useTranslate } from '@almadar/ui/hooks';

// ============================================================================
// State Machine
// ============================================================================

export type HomeSLHomeStockBlocksState =
  | 'idle';

export type HomeSLHomeStockBlocksEvent =
  | 'INIT'
  | 'SHOW_BLOCKS';

type HomeSLHomeStockBlocksSlot = 'main';
interface HomeSLHomeStockBlocksInternalState {
  machineState: HomeSLHomeStockBlocksState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: HomeSLHomeStockBlocksEvent | null;
  slotWriter: Partial<Record<HomeSLHomeStockBlocksSlot, HomeSLHomeStockBlocksEvent | null>>;
  loading: boolean;
  pendingEvent: HomeSLHomeStockBlocksEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<HomeSLAssemblyViewEntity>;
}

type HomeSLHomeStockBlocksAction =
  | { type: 'EVENT_SUCCESS'; event: HomeSLHomeStockBlocksEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: HomeSLHomeStockBlocksSlot[]; renderClear?: HomeSLHomeStockBlocksSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: HomeSLHomeStockBlocksEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof HomeSLAssemblyViewEntity & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function homeSLHomeStockBlocksReducer(
  state: HomeSLHomeStockBlocksInternalState,
  action: HomeSLHomeStockBlocksAction
): HomeSLHomeStockBlocksInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as HomeSLHomeStockBlocksState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<HomeSLAssemblyViewEntity>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<HomeSLAssemblyViewEntity>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<HomeSLAssemblyViewEntity>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: HomeSLHomeStockBlocksInternalState = {
  machineState: 'idle',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

export interface HomeSLHomeStockBlocksConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  blocks: { icon: string; label: string[] }[];
  body: { "aria-label": string; children: Array<{ align: string; children: Array<{ children: { className: string; name: string; size: string; type: string }[]; className: string; type: string } | { className: string; color: string; content: string; type: string; variant: string } | { color: string; content: string; type: string; variant: string; weight: string }>; className: Array<Array<Array<Array<number | string> | Array<string | string[]> | string> | string> | string>; direction: string; gap: string; type: string } | { children: Array<Array<string | { align: string; children: Array<{ children: { name: string; size: string; type: string }[]; className: Array<Array<Array<number | string> | string> | string>; type: string } | { className: string; color: string; content: Array<Array<number | string> | string>; type: string; variant: string } | { content: string; type: string; variant: string; weight: Array<Array<Array<number | string> | string> | string> }>; className: Array<Array<Array<Array<number | string> | string | string[]> | string> | string>; direction: string; gap: string; type: string }> | string>; direction: string; gap: string; type: string }>; className: string; direction: string; gap: string; type: string };
  initialShown: number;
  result?: string;
  resultIcon: string;
}

const DEFAULT_HOME_SL_HOME_STOCK_BLOCKS_CONFIG = {
  blocks: [{ icon: "table", label: ["i18n/t", "std-almadar-home:blocks.stockTable"] }, { icon: "area-chart", label: ["i18n/t", "std-almadar-home:blocks.stockChart"] }, { icon: "gauge", label: ["i18n/t", "std-almadar-home:blocks.kpis"] }, { icon: "layout-dashboard", label: ["i18n/t", "std-almadar-home:blocks.shell"] }],
  body: { "aria-label": "@config.result", children: [{ children: ["array/map", "@config.blocks", ["fn", "block", { align: "center", children: [{ className: "w-4 text-center", color: "muted", content: ["if", ["==", "@index", 0], "", "+"], type: "typography", variant: "small" }, { children: [{ name: "@block.icon", size: "sm", type: "icon" }], className: ["if", ["==", "@index", ["-", "@entity.shown", 1]], "w-7 h-7 rounded-md border border-primary bg-primary/10 ring-2 ring-primary/30 flex items-center justify-center transition-all duration-500", "w-7 h-7 rounded-md border border-border bg-muted flex items-center justify-center transition-all duration-500"], type: "box" }, { content: "@block.label", type: "typography", variant: "body", weight: ["if", ["==", "@index", ["-", "@entity.shown", 1]], "semibold", "normal"] }], className: ["str/concat", "transition-opacity duration-500 ", ["if", ["or", ["<", "@entity.shown", 0], ["<", "@index", "@entity.shown"]], "opacity-100", "opacity-30"]], direction: "horizontal", gap: "sm", type: "stack" }]], direction: "vertical", gap: "xs", type: "stack" }, { align: "center", children: [{ className: "w-4 text-center", color: "muted", content: "=", type: "typography", variant: "small" }, { children: [{ className: "text-primary-foreground", name: "@config.resultIcon", size: "sm", type: "icon" }], className: "w-7 h-7 rounded-md bg-primary flex items-center justify-center", type: "box" }, { color: "primary", content: "@config.result", type: "typography", variant: "body", weight: "bold" }], className: ["str/concat", "pt-1 transition-opacity duration-500 ", ["if", ["or", ["<", "@entity.shown", 0], [">=", "@entity.shown", ["array/len", "@config.blocks"]]], "opacity-100", "opacity-30"]], direction: "horizontal", gap: "sm", type: "stack" }], className: "pt-2", direction: "vertical", gap: "xs", type: "stack" },
  initialShown: -1,
  resultIcon: "package",
} as HomeSLHomeStockBlocksConfig;

export interface HomeSLHomeStockBlocksLogicReturn {
  state: HomeSLHomeStockBlocksState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: HomeSLHomeStockBlocksEvent | null;
  slotWriter: Partial<Record<HomeSLHomeStockBlocksSlot, HomeSLHomeStockBlocksEvent | null>>;
  loading: boolean;
  pendingEvent: HomeSLHomeStockBlocksEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<HomeSLAssemblyViewEntity>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof HomeSLHomeStockBlocksEventPayloadMap]: (payload?: HomeSLHomeStockBlocksEventPayloadMap[K]) => Promise<EventResponse> };
  config: HomeSLHomeStockBlocksConfig;
}

export function useHomeSLHomeStockBlocksLogic(config?: Partial<HomeSLHomeStockBlocksConfig>): HomeSLHomeStockBlocksLogicReturn {
  const mergedConfig: HomeSLHomeStockBlocksConfig = { ...DEFAULT_HOME_SL_HOME_STOCK_BLOCKS_CONFIG, ...(config ?? {}) };

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/home-sl-home-stock-blocks', orbitalName: 'HomeSL', traitName: 'HomeSLHomeStockBlocks' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(homeSLHomeStockBlocksReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  const { t: __t, locale: __locale } = useTranslate();
  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const payloadObj = payload;
    const entity: EntityRow[] = liveData['HomeSLAssemblyView'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};


    if (fromState === 'idle' && event === 'INIT') {
      // idle → idle (on INIT)
        _fields['shown'] = -1;
        fields = { ...(fields ?? {}), 'shown': _fields['shown'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'SHOW_BLOCKS') {
      // idle → idle (on SHOW_BLOCKS)
        _fields['shown'] = payloadObj?.count;
        fields = { ...(fields ?? {}), 'shown': _fields['shown'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'idle';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: HomeSLHomeStockBlocksSlot[]; clear: HomeSLHomeStockBlocksSlot[] } => {
    switch (fromState) {
      case 'idle':
        switch (event) {
          case 'INIT': return { write: ['main'] as HomeSLHomeStockBlocksSlot[], clear: [] as HomeSLHomeStockBlocksSlot[] };
          case 'SHOW_BLOCKS': return { write: ['main'] as HomeSLHomeStockBlocksSlot[], clear: [] as HomeSLHomeStockBlocksSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'HomeSLAssemblyView': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as HomeSLHomeStockBlocksEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    void sendEvent; void commandPump; // no server-touching effect on this trait; shared-store commit above is the only sync needed
    return { success: true, transitioned: true, states: { 'HomeSLHomeStockBlocks': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof HomeSLHomeStockBlocksEventPayloadMap]: (payload?: HomeSLHomeStockBlocksEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'SHOW_BLOCKS': (payload?: HomeSLHomeStockBlocksEventPayloadMap['SHOW_BLOCKS']) => dispatch('SHOW_BLOCKS', payload),
  };

  useUIEvents(enqueueEvent, 'HomeSL.HomeSLHomeStockBlocks', ['INIT', 'SHOW_BLOCKS'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('HomeSLHomeStockBlocks', (): TraitStateSnapshot => ({
      traitName: 'HomeSLHomeStockBlocks',
      currentState: internalStateRef.current.machineState,
      states: ['idle'],
      events: ['INIT', 'SHOW_BLOCKS'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: [],
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  mergedConfig.result = mergedConfig.result ?? (__t("std-almadar-home:result.inventory"));
  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
